<!-- PersonalInfoCard.vue -->
<template>
  <div class="personal-info-card">
    <div class="card-header">
      <!-- Editable Name -->
      <div class="name-wrapper">
        <input
          v-if="editingField === 'name'"
          ref="nameInput"
          :value="personInfo.legalFirstName"
          @input="updateField('legalFirstName', $event.target.value)"
          @blur="stopEditing"
          @keydown.enter="stopEditing"
          @keydown.escape="stopEditing"
          placeholder="Enter name"
          class="name-input editing"
        />
        <h5
          v-else
          @click.stop="startEditing('name')"
          class="person-name clickable"
          :class="{ empty: !personInfo.legalFirstName }"
        >
          {{ personInfo.legalFirstName || 'Enter name' }}
        </h5>
      </div>

      <button v-if="personType === 'spouse'" class="remove-button" @click="removeSpouse" title="Remove spouse">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
        </svg>
      </button>
    </div>

    <div class="info-grid">
      <!-- Birth Date -->
      <div class="info-item">
        <span class="info-label">Birth Date:</span>
        <input
          v-if="editingField === 'birthDate'"
          ref="birthDateInput"
          :value="formatDateForInput"
          @input="updateField('dateOfBirth', $event.target.value)"
          @blur="stopEditing"
          @keydown.enter="stopEditing"
          @keydown.escape="stopEditing"
          type="date"
          class="date-input editing"
        />
        <span
          v-else
          @click.stop="startEditing('birthDate')"
          class="info-value clickable"
          :class="{ empty: !personInfo.dateOfBirth }"
        >
          {{ formatDateForDisplay || 'Click to set' }}
        </span>
      </div>

      <!-- Age (Read-only computed) -->
      <div class="info-item">
        <span class="info-label">Age:</span>
        <span class="info-value readonly">{{ currentAge || '?' }}</span>
      </div>
      
      <!-- Retirement Age -->
      <div class="info-item">
        <span class="info-label">Retirement Age:</span>
        <input
          v-if="editingField === 'retirementAge'"
          ref="retirementAgeInput"
          :value="personInfo.targetRetirementAge"
          @input="updateField('targetRetirementAge', Number($event.target.value) || null)"
          @blur="stopEditing"
          @keydown.enter="stopEditing"
          @keydown.escape="stopEditing"
          type="number"
          min="50"
          max="80"
          placeholder="65"
          class="number-input editing"
        />
        <span
          v-else
          @click.stop="startEditing('retirementAge')"
          class="info-value clickable"
          :class="{ empty: !personInfo.targetRetirementAge }"
        >
          {{ personInfo.targetRetirementAge || 'Click to set' }}
        </span>
      </div>

      <!-- Life Expectancy -->
      <div class="info-item">
        <span class="info-label">Life Exp:</span>
        <input
          v-if="editingField === 'lifeExpectancy'"
          ref="lifeExpectancyInput"
          :value="personInfo.lifeExpectancy"
          @input="updateField('lifeExpectancy', Number($event.target.value) || null)"
          @blur="stopEditing"
          @keydown.enter="stopEditing"
          @keydown.escape="stopEditing"
          type="number"
          min="60"
          max="120"
          class="number-input editing"
        />
        <span
          v-else
          @click.stop="startEditing('lifeExpectancy')"
          class="info-value clickable"
          :class="{ empty: !personInfo.lifeExpectancy }"
        >
          {{ personInfo.lifeExpectancy || 'Click to set' }}
        </span>
      </div>

      <!-- Retirement Info (Read-only computed) -->
      <div v-if="showRetirementInfo" class="info-item retirement-info">
        <span class="info-label">Retire in:</span>
        <span class="info-value readonly">{{ yearsToRetirement }} yrs ({{ retirementYear }})</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PersonalInfoCard',
  props: {
    personInfo: {
      type: Object,
      required: true
    },
    personName: {
      type: String,
      required: true
    },
    personType: {
      type: String,
      required: true,
      validator: value => ['primary', 'spouse'].includes(value)
    }
  },
  emits: ['update-personal-info', 'remove-spouse'],
  data() {
    return {
      editingField: null,
      currentYear: new Date().getFullYear()
    };
  },
  computed: {
    formatDateForInput() {
      if (!this.personInfo.dateOfBirth) return '';
      
      try {
        const date = new Date(this.personInfo.dateOfBirth);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (error) {
        const yearMatch = String(this.personInfo.dateOfBirth).match(/\b(19|20)\d{2}\b/);
        if (yearMatch) {
          return `${yearMatch[0]}-01-01`;
        }
      }
      
      return '';
    },

    formatDateForDisplay() {
      if (!this.personInfo.dateOfBirth) return '';
      
      try {
        const date = new Date(this.personInfo.dateOfBirth);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          });
        }
      } catch (error) {
        // If we can't parse the date, just return the raw value
        return String(this.personInfo.dateOfBirth);
      }
      
      return '';
    },

    currentAge() {
      if (!this.personInfo.dateOfBirth) return null;
      
      try {
        let birthYear;
        const dateOfBirth = this.personInfo.dateOfBirth;
        
        if (typeof dateOfBirth === 'string') {
          const date = new Date(dateOfBirth);
          if (!isNaN(date.getTime())) {
            birthYear = date.getFullYear();
          } else {
            const yearMatch = dateOfBirth.match(/\b(19|20)\d{2}\b/);
            if (yearMatch) {
              birthYear = parseInt(yearMatch[0]);
            }
          }
        } else if (typeof dateOfBirth === 'number') {
          birthYear = dateOfBirth;
        }
        
        if (birthYear && birthYear >= 1900 && birthYear <= this.currentYear) {
          return this.currentYear - birthYear;
        }
      } catch (error) {
        console.error('Error calculating age:', error);
      }
      
      return null;
    },

    yearsToRetirement() {
      if (!this.currentAge || !this.personInfo.targetRetirementAge) return null;
      const years = this.personInfo.targetRetirementAge - this.currentAge;
      return years > 0 ? years : 0;
    },

    retirementYear() {
      if (!this.yearsToRetirement === null) return null;
      return this.currentYear + this.yearsToRetirement;
    },

    showRetirementInfo() {
      return this.currentAge && this.personInfo.targetRetirementAge;
    }
  },
  methods: {
    startEditing(field) {
      console.log('Starting edit for field:', field); // Debug log
      this.editingField = field;

      this.$nextTick(() => {
        // Add click outside listener
        setTimeout(() => {
          document.addEventListener('click', this.handleClickOutside);
        }, 0);

        const refNames = {
          name: 'nameInput',
          birthDate: 'birthDateInput',
          retirementAge: 'retirementAgeInput',
          lifeExpectancy: 'lifeExpectancyInput'
        };
        
        const refEl = this.$refs[refNames[field]];
        if (refEl) {
          refEl.focus();
          if (field === 'name' && refEl.select) {
            refEl.select();
          }
        }
      });
    },

    stopEditing() {
      console.log('Stopping edit'); // Debug log
      this.editingField = null;
      document.removeEventListener('click', this.handleClickOutside);
    },

    handleClickOutside(event) {
      // Check if the click is outside the personal-info-card
      const card = this.$el;
      if (card && !card.contains(event.target)) {
        this.stopEditing();
      }
    },

    updateField(field, value) {
      console.log('Updating field:', field, 'with value:', value); // Debug log
      this.$emit('update-personal-info', this.personType, field, value);
    },

    removeSpouse() {
      this.$emit('remove-spouse');
      // Alternative if the parent expects it differently:
      // this.$emit('update-personal-info', 'spouse', 'remove', true);
    }
  },
  beforeUnmount() {
    // Vue 2.7 uses beforeUnmount instead of beforeDestroy
    document.removeEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // Fallback for Vue 2.6 and earlier
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.personal-info-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  padding: 1rem;
  transition: all 0.2s ease;
  position: relative;
}

.personal-info-card:hover {
  border-color: #c0c0c0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.5rem;
}

.name-wrapper {
  flex: 1;
}

.person-name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  padding: 2px 4px;
  border-radius: 3px;
  transition: all 0.2s ease;
}

.person-name.empty {
  color: #adb5bd;
  font-style: italic;
  font-weight: 400;
}

.name-input {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  border: 1px solid #007bff;
  border-radius: 3px;
  padding: 2px 4px;
  background: #fff;
  width: 100%;
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.25);
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
  padding: 2px 4px;
  border-radius: 3px;
  transition: all 0.2s ease;
  min-height: 1.2rem;
  display: flex;
  align-items: center;
}

.info-value.empty {
  color: #adb5bd;
  font-style: italic;
  font-weight: 400;
}

.info-value.readonly {
  color: #495057;
  background: #f8f9fa;
  padding: 4px 6px;
  border-radius: 3px;
  font-weight: 500;
  cursor: default !important;
}

.info-value.clickable {
  cursor: pointer;
}

.info-value.clickable:hover {
  background: #f0f8ff;
  outline: 1px solid #e3f2fd;
}

/* Input styling */
.date-input, .number-input {
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
  border: 1px solid #007bff;
  border-radius: 3px;
  padding: 4px 6px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.25);
  min-height: 1.2rem;
}

.number-input {
  text-align: center;
  width: 80px;
}

/* Special styling for retirement info */
.retirement-info {
  grid-column: 1 / -1;
  padding-top: 0.5rem;
  border-top: 1px solid #e9ecef;
}

.retirement-info .info-value {
  font-weight: 600;
  color: #28a745;
}

/* Remove button */
.remove-button {
  background: none;
  border: none;
  color: #adb5bd;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-button:hover {
  color: #e74c3c;
  background: #fff5f5;
}

/* Clickable elements */
.clickable {
  cursor: pointer;
  user-select: none;
}

/* Input focus styles */
input:focus {
  outline: none;
}

/* Remove number input spinners */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .retirement-info {
    grid-column: 1;
  }
}
</style>